// Simple global game tracking - no complex React state management
// This eliminates timing issues and makes the system much more reliable

// Global variables for game tracking
let playerStats = {
  topWords: [],
  metersSkatedRecord: 0,
  totalMeters: 0,
  totalScore: 0,
  bestGameScore: 0
};

let currentGameStats = {
  words: [],
  meters: 0,
  totalPoints: 0
};

/**
 * Simple game session management without React state complexity
 * Uses global variables for reliable data tracking
 */
export function useGameSession() {

  /**
   * Initialize game with player stats from Firebase
   * @param {Object} playerData - Player data from Firestore (topWords, metersSkatedRecord)
   */
  const initializeSession = (playerData = null) => {
    console.log('🎮 Initializing game with player stats');

    // Load player stats into global variable
    playerStats = playerData || {
      topWords: [],
      metersSkatedRecord: 0,
      totalMeters: 0,
      totalScore: 0,
      bestGameScore: 0
    };

    // Reset current game stats
    currentGameStats = {
      words: [],
      meters: 0,
      totalPoints: 0
    };

    console.log('✅ Game initialized with player stats:', {
      topWordsCount: playerStats.topWords.length,
      metersRecord: playerStats.metersSkatedRecord,
      totalMeters: playerStats.totalMeters,
      totalScore: playerStats.totalScore,
      bestGameScore: playerStats.bestGameScore
    });
  };

  /**
   * Add a word to current game stats
   * @param {string} word - The submitted word
   * @param {number} points - Points scored for the word
   */
  const addSessionWord = (word, points) => {
    const wordEntry = {
      word: word.toUpperCase(),
      points: points,
      timestamp: new Date()
    };

    currentGameStats.words.push(wordEntry);
    currentGameStats.totalPoints += points;

    console.log('✅ Word added to game:', {
      word: wordEntry.word,
      points,
      totalWords: currentGameStats.words.length,
      totalPoints: currentGameStats.totalPoints
    });
  };

  /**
   * Update current game meters
   * @param {number} meters - Current meters skated value
   */
  const updateSessionMeters = (meters) => {
    currentGameStats.meters = meters;

    // Only log occasionally to avoid spam (every 50 meters)
    if (Math.floor(meters) % 50 === 0 && Math.floor(meters) > 0) {
      console.log('🔍 Meters updated:', Math.floor(meters));
    }
  };

  /**
   * Check if current game has new meters record
   */
  const hasNewMetersRecord = () => {
    const currentMeters = Math.floor(currentGameStats.meters);
    const recordMeters = playerStats.metersSkatedRecord || 0; // Handle null/undefined
    return currentMeters > recordMeters;
  };

  /**
   * Check if current game has new best game score
   */
  const hasNewBestGameScore = () => {
    const currentGameScore = currentGameStats.totalPoints;
    const bestGameScore = playerStats.bestGameScore || 0; // Handle null/undefined
    return currentGameScore > bestGameScore;
  };

  /**
   * Calculate updated top words list
   * Preserves original timestamps for existing words, assigns current timestamp to new words
   */
  const calculateUpdatedTopWords = () => {
    // Create a map to track existing words and their timestamps
    const existingWordsMap = new Map(
      playerStats.topWords.map(word => [`${word.word}-${word.points}`, word])
    );

    // Merge all words, preserving timestamps for existing ones
    const allWords = [
      ...playerStats.topWords, // Keep existing words with original timestamps
      ...currentGameStats.words.map(word => {
        const wordKey = `${word.word}-${word.points}`;
        const existingWord = existingWordsMap.get(wordKey);

        // If this exact word/points combo already exists, preserve its original timestamp
        if (existingWord) {
          return {
            ...word,
            timestamp: existingWord.timestamp
          };
        }

        // New word keeps its current timestamp
        return word;
      })
    ];

    // Remove duplicates (same word and points) - keep the one with earlier timestamp
    const uniqueWords = [];
    const seenWords = new Set();

    for (const word of allWords) {
      const wordKey = `${word.word}-${word.points}`;
      if (!seenWords.has(wordKey)) {
        seenWords.add(wordKey);
        uniqueWords.push(word);
      }
    }

    // Sort by points (descending) and keep top 10
    return uniqueWords
      .sort((a, b) => b.points - a.points)
      .slice(0, 10);
  };

  /**
   * Check if any current game words made it to top words
   */
  const hasNewTopWords = () => {
    if (currentGameStats.words.length === 0) return false;

    const updatedTopWords = calculateUpdatedTopWords();

    // Check if any current game words are in the updated top words
    return currentGameStats.words.some(gameWord =>
      updatedTopWords.some(topWord =>
        topWord.word === gameWord.word && topWord.points === gameWord.points
      )
    );
  };

  /**
   * Get Firebase updates needed based on current game vs player stats
   * Returns null if no updates are needed
   */
  const getFirebaseUpdates = () => {
    console.log('🔍 Calculating Firebase updates...');
    console.log('🔍 Current game stats:', {
      words: currentGameStats.words.length,
      meters: Math.floor(currentGameStats.meters),
      totalPoints: currentGameStats.totalPoints
    });
    console.log('🔍 Player stats:', {
      topWordsCount: playerStats.topWords.length,
      metersRecord: playerStats.metersSkatedRecord,
      totalMeters: playerStats.totalMeters,
      totalScore: playerStats.totalScore,
      bestGameScore: playerStats.bestGameScore
    });

    const updates = {};
    let hasUpdates = false;

    // Check for new meters record
    const isNewMetersRecord = hasNewMetersRecord();
    if (isNewMetersRecord) {
      updates.metersSkatedRecord = Math.floor(currentGameStats.meters);
      hasUpdates = true;
      console.log('🔍 New meters record:', Math.floor(currentGameStats.meters));
    }

    // Always add total meters (cumulative)
    if (currentGameStats.meters > 0) {
      updates.totalMeters = Math.floor(currentGameStats.meters);
      hasUpdates = true;
      console.log('🔍 Game meters to add to total:', Math.floor(currentGameStats.meters));
    }

    // Always add total score (cumulative)
    if (currentGameStats.totalPoints > 0) {
      updates.totalScore = currentGameStats.totalPoints;
      hasUpdates = true;
      console.log('🔍 Game score to add to total:', currentGameStats.totalPoints);
    }

    // Check for new best game score
    const isNewBestGameScore = hasNewBestGameScore();
    if (isNewBestGameScore) {
      updates.bestGameScore = currentGameStats.totalPoints;
      hasUpdates = true;
      console.log('🔍 New best game score:', currentGameStats.totalPoints);
    }

    // Check for new top words
    const isNewTopWords = hasNewTopWords();
    if (isNewTopWords) {
      updates.topWords = calculateUpdatedTopWords();
      hasUpdates = true;
      console.log('🔍 New top words:', updates.topWords.length);
    }

    const result = hasUpdates ? updates : null;
    console.log('🔍 Firebase updates result:', result);
    return result;
  };

  /**
   * Get current game statistics for display
   */
  const getSessionStats = () => {
    const isNewMetersRecord = hasNewMetersRecord();
    const isNewBestGameScore = hasNewBestGameScore();
    const isNewTopWords = hasNewTopWords();

    const stats = {
      wordsSubmitted: currentGameStats.words.length,
      totalPoints: currentGameStats.totalPoints,
      bestWord: currentGameStats.words.length > 0
        ? currentGameStats.words.reduce((best, word) => word.points > best.points ? word : best)
        : null,
      metersSkated: Math.floor(currentGameStats.meters),
      isNewMetersRecord: isNewMetersRecord,
      isNewBestGameScore: isNewBestGameScore,
      newTopWordsCount: isNewTopWords ? currentGameStats.words.length : 0
    };

    console.log('📊 Game stats:', stats);
    return stats;
  };

  /**
   * Update global player stats after Firebase batch update
   * @param {Object} updateResults - Results from Firebase batch update
   */
  const updatePlayerStats = (updateResults) => {
    console.log('🔄 Updating global player stats with Firebase results');

    // Update meters record if it was updated
    if (updateResults.metersRecord.updated) {
      playerStats.metersSkatedRecord = updateResults.metersRecord.newRecord;
      console.log('✅ Updated global metersSkatedRecord:', playerStats.metersSkatedRecord);
    }

    // Update total meters if it was updated
    if (updateResults.totalMeters.updated) {
      playerStats.totalMeters = updateResults.totalMeters.newTotal;
      console.log('✅ Updated global totalMeters:', playerStats.totalMeters);
    }

    // Update total score if it was updated
    if (updateResults.totalScore.updated) {
      playerStats.totalScore = updateResults.totalScore.newTotal;
      console.log('✅ Updated global totalScore:', playerStats.totalScore);
    }

    // Update best game score if it was updated
    if (updateResults.bestGameScore.updated) {
      playerStats.bestGameScore = updateResults.bestGameScore.newBest;
      console.log('✅ Updated global bestGameScore:', playerStats.bestGameScore);
    }

    // Update top words if they were updated
    if (updateResults.topWords.updated) {
      // The topWords are already calculated in getFirebaseUpdates()
      // We need to get them from the current calculation
      playerStats.topWords = calculateUpdatedTopWords();
      console.log('✅ Updated global topWords:', playerStats.topWords.length, 'words');
    }

    console.log('🔄 Global player stats update completed');
  };

  /**
   * Reset current game stats (for new game)
   */
  const resetSession = () => {
    console.log('🔄 Resetting game stats for new game');

    currentGameStats = {
      words: [],
      meters: 0,
      totalPoints: 0
    };

    console.log('🔄 Game stats reset completed');
  };

  // Return simple interface - no React state, just functions
  return {
    // Game data access
    get sessionWords() { return currentGameStats.words; },
    get sessionMeters() { return currentGameStats.meters; },
    get initialPlayerData() { return playerStats; },

    // Game management functions
    initializeSession,
    addSessionWord,
    updateSessionMeters,
    updatePlayerStats,
    resetSession,

    // Calculation functions
    hasNewMetersRecord,
    hasNewBestGameScore,
    hasNewTopWords,
    calculateUpdatedTopWords,
    getFirebaseUpdates,
    getSessionStats
  };
}
